// The small controls every table of the questionnaire editor is made of.

import { Fragment, useId, useRef } from "react";
import type { IntakeCategory, IntakeLanguage, IntakeText } from "@duralex/shared";
import { languageName } from "../../../../lib/intakeLanguages";
import type { Behind } from "../../lib/intakeLogic";
import { GROUPS, withText } from "./draft";

/** English plus the second language, side by side. A blank second
    language removes the key: the server refuses an empty string. An out
    of date translation is marked beside its field, in the muted state
    colour - it is a fact about the office's text, not the model's. A
    cell a model wrote wears the AI chip beside the field, never on it;
    the chip goes when the office retypes the cell. */
export function LabelField({
  label,
  text,
  lang,
  onChange,
  multiline = false,
  stale = false,
  ai = false,
}: {
  label: string;
  text: IntakeText;
  lang: IntakeLanguage;
  onChange: (text: IntakeText) => void;
  multiline?: boolean;
  /** The English moved after this translation was written. */
  stale?: boolean;
  /** The second language is a model's proposal, not yet saved. */
  ai?: boolean;
}) {
  const alt = languageName(lang);
  const said = `A model proposed this ${alt}; retype it to make it yours`;
  const staleId = useId();
  const aiId = useId();
  const field = (l: IntakeLanguage, name: string) => {
    const props = {
      className: multiline ? "cc-field ie-text" : "cc-field",
      "aria-label": `${label} (${name})`,
      "aria-describedby":
        l === "en"
          ? undefined
          : [ai ? aiId : "", stale ? staleId : ""].filter(Boolean).join(" ") || undefined,
      value: text[l] ?? "",
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChange(withText(text, l, e.target.value)),
      placeholder: l === "en" ? "" : `${name} - blank means not translated`,
      maxLength: 1000,
    };
    return multiline ? <textarea rows={2} {...props} /> : <input type="text" {...props} />;
  };
  return (
    <div className="ie-label">
      {field("en", "English")}
      <div className="ie-alt">
        {field(lang, alt)}
        {ai && (
          <span id={aiId} className="ai-chip" title={said} data-testid={`ai:${label}`}>
            <span aria-hidden="true">AI</span>
            <span className="sr-only">{said}</span>
          </span>
        )}
        {stale && (
          <span
            id={staleId}
            className="ie-stale"
            title={`The English changed after this ${alt} was written`}
            data-testid={`stale:${label}`}
          >
            Out of date
          </span>
        )}
      </div>
    </div>
  );
}

/** What the shown second language is behind by in a part of the form,
    in the two phrases the language list uses - "N not translated",
    "N out of date"; nothing while the part is up to date. The space
    before each pill keeps it a word of its own in a tab's name. */
export function BehindPills({ behind, className }: { behind: Behind; className: string }) {
  const parts = [
    behind.untranslated > 0 ? `${behind.untranslated} not translated` : "",
    behind.outOfDate > 0 ? `${behind.outOfDate} out of date` : "",
  ].filter(Boolean);
  return (
    <>
      {parts.map((p) => (
        <Fragment key={p}>
          {" "}
          <span className={className}>{p}</span>
        </Fragment>
      ))}
    </>
  );
}

/** Which case types a question or a document is for: everyone, a group
    (every case type in it, now and later), or named case types. */
export function CodeChips({
  label,
  value,
  categories,
  onToggle,
}: {
  label: string;
  value: string[];
  categories: IntakeCategory[];
  onToggle: (code: string) => void;
}) {
  const chip = (code: string, text: string, kind: "all" | "group" | "code") => {
    const on = value.includes(code);
    return (
      <button
        key={`${kind}:${code}`}
        type="button"
        role="checkbox"
        aria-checked={on}
        aria-label={`${label}: ${text}`}
        className={`ie-chip${on ? " on" : ""}${kind === "group" ? " group" : ""}`}
        onClick={() => onToggle(code)}
      >
        {text}
      </button>
    );
  };
  return (
    <div className="ie-chips" role="group" aria-label={label}>
      {chip("ALL", "Everyone", "all")}
      {GROUPS.map((g) => chip(g, `${g} group`, "group"))}
      {categories.map((c) => chip(c.code, c.code, "code"))}
    </div>
  );
}

/** Up, down, remove - the whole reorder story, keyboard first. The
    moved row keeps the focus: the browser drops it when React moves the
    node, so it is put back once the move has rendered. */
export function RowActions({
  what,
  onUp,
  onDown,
  onRemove,
  locked,
}: {
  what: string;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
  /** Why the row may not go, when it may not. */
  locked?: string;
}) {
  const up = useRef<HTMLButtonElement>(null);
  const down = useRef<HTMLButtonElement>(null);
  const keep = (ref: typeof up) => setTimeout(() => ref.current?.focus(), 0);
  return (
    <div className="ie-actions">
      <button
        ref={up}
        type="button"
        className="cc-link-btn"
        aria-label={`Move ${what} up`}
        onClick={() => {
          onUp();
          keep(up);
        }}
      >
        Up
      </button>
      <button
        ref={down}
        type="button"
        className="cc-link-btn"
        aria-label={`Move ${what} down`}
        onClick={() => {
          onDown();
          keep(down);
        }}
      >
        Down
      </button>
      <button
        type="button"
        className="cc-link-btn danger"
        aria-label={`Remove ${what}`}
        disabled={Boolean(locked)}
        title={locked}
        onClick={onRemove}
      >
        Remove
      </button>
    </div>
  );
}

/** An id the office may still choose (a new row) or one that is set. */
export function IdField({
  value,
  onChange,
  locked,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  locked: boolean;
  label: string;
}) {
  return (
    <input
      type="text"
      className="cc-field ie-id"
      aria-label={label}
      value={value}
      readOnly={locked}
      title={locked ? "Set once saved: answers and files are keyed by it" : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
